import {
  Component,
  signal,
  computed,
  Input,
  input,
  SimpleChanges,
  EventEmitter,
  Output
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { TareaDto } from '@models/tarea/tareaDto.model';
import { MatTableDataSource } from '@angular/material/table';
import { AvatarInicialesComponent } from "../../../../component/avatar-iniciales/avatar-iniciales.component";
import { AccionesDialog, EstadosTareas, PrioridadTarea } from '@models/base/identificadores.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogParams } from 'src/app/interfaces/dialogParam.interface.model';
import { DialogFormTareaComponent } from '@views/tareas/dialog-form-tarea/dialog-form-tarea.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { EstadoTareaService } from '@services/estadoTarea/estadoTarea.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TareaService } from '@services/tarea/tarea.service';
import { TiposTareaService } from '@services/tipoTarea/tipoTarea.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { BaseDto } from '@models/base/baseDto.model';
import { DtoBase } from '@models/base/dtoBase.model';

export interface KanbanColumn {
  estadoTarea: DtoBase;
  tarea: TareaDto[];
}

const statusMapperColors = {
  Medium: 'bg-yellow-100 text-yellow-800',
  High: 'bg-red-100 text-red-800',
  Low: 'bg-green-100 text-green-800',
};

const statusMapperIcons = {
  Medium: 'priority_medium',
  High: 'priority_high',
  Low: 'low_priority',
};

@Component({
  selector: 'app-tablero-kanban',
  standalone: true,
  templateUrl: 'tableroKanban.component.html',
  styleUrl: './tableroKanban.component.css',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    CdkDrag,
    CdkDropList,
    CdkDropListGroup,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    MatRippleModule,
    AvatarInicialesComponent,
    NgxSpinnerModule
  ],

})
export class TableroKanbanComponent {

  @Output() emitirGenerarListado = new EventEmitter<void>();
  columns = signal<KanbanColumn[]>([]);
  @Input() listadoTareas!: any[]

  estadosTareas = EstadosTareas;
  prioridadTarea = PrioridadTarea;
  idExpediente: any;
  accionesDialog = AccionesDialog;
  estadosTarea!: BaseDto[];

  constructor(
    private tareaService: TareaService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
    private estadoTareaService: EstadoTareaService,
    private dialog: MatDialog
  ) { }


  ngOnInit(changes: SimpleChanges): void {
    this.getAllEstadosTarea()
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.getAllEstadosTarea()
  }

  listDrop(event: CdkDragDrop<undefined>) {
    const { previousIndex, currentIndex } = event;
    moveItemInArray(this.columns()!, previousIndex, currentIndex);
  }



  drop(event: CdkDragDrop<TareaDto[]>) {
    const { previousIndex, currentIndex, container, previousContainer } = event;

    if (container === previousContainer) {
      moveItemInArray(container.data, previousIndex, currentIndex);
      return; // no cambió de columna, no hace falta actualizar estado
    }

    transferArrayItem(
      previousContainer.data,
      container.data,
      previousIndex,
      currentIndex
    );

    const tareaMovida = container.data[currentIndex];

    const containerElement = container.element.nativeElement as HTMLElement;
    const previousContainerElement = previousContainer.element.nativeElement as HTMLElement;

    const idEstadoDestino = Number(containerElement.getAttribute('data-estado-id'));
    const idEstadoOrigen = Number(previousContainerElement.getAttribute('data-estado-id'));

    // Si realmente cambió de estado
    if (tareaMovida && idEstadoDestino && idEstadoOrigen !== idEstadoDestino) {
      this.modificarEstado(tareaMovida.id, idEstadoDestino);
    }
  }


  openDialog<T>(params: DialogParams<T>) {
    const { accion, entidad } = params;

    const data = {
      ...entidad,
      accion,
      idExpediente: this.idExpediente
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = false
    dialogConfig.width = "50%"
    dialogConfig.data = data

    const dialogRef = this.dialog.open(DialogFormTareaComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result || !result.event) return;

      switch (result.event) {
        case AccionesDialog.Agregar:

          this.emitirGenerarListado.emit();
          break;
        case AccionesDialog.Modificar:

          this.emitirGenerarListado.emit();
          break;
        case AccionesDialog.Eliminar:

          this.emitirGenerarListado.emit();
          break;
        default:

          break;
      }

    });
  }

  getAllEstadosTarea() {
    this.spinner.show('spTarea')
    this.estadoTareaService.getAll()
      .subscribe(
        data => {
          this.spinner.hide('spTarea')
          this.estadosTarea = data.sort((a, b) => a.id - b.id);


          if (this.listadoTareas) {
            this.columns.set(this.agruparPorEstado(this.listadoTareas));
          }
        },
        error => {
          this.spinner.hide('spTarea')
          this.snackBar.showError(error, "Error");
        })
  }

  getColor(priority: 'High' | 'Medium' | 'Low'): string {
    return statusMapperColors[priority];
  }

  getIcon(priority: 'High' | 'Medium' | 'Low'): string {
    return statusMapperIcons[priority];
  }

  esHoy(fecha: string | Date): boolean {
    const hoy = new Date();
    const fechaFin = new Date(fecha);
    var esHoy = (
      hoy.getDate() === fechaFin.getDate() &&
      hoy.getMonth() === fechaFin.getMonth() &&
      hoy.getFullYear() === fechaFin.getFullYear()
    )
    return esHoy;
  }

  modificarEstado(idTarea: number, isEstadoTarea: number) {
    this.spinner.show("spTarea")
    this.tareaService.updateEstadoTarea(idTarea, isEstadoTarea)
      .subscribe(
        data => {
          this.spinner.hide("spTarea")
          this.snackBar.showSuccess('Registro modificado correctamente.', "Exito");
        },
        error => {
          this.spinner.hide("spTarea")
          this.snackBar.showError(error, "Error");
        })
  }

  iniciales(nombre: string): string {
    return nombre
      .split(' ')
      .map(p => p.charAt(0))
      .join('')
      .toUpperCase();
  }

  editarTicket(ticket: any) {
    console.log('Editar:', ticket);
  }

  eliminarTicket(ticket: any) {
    console.log('Eliminar:', ticket);
  }

  verDetalle(ticket: any) {
    console.log('Detalle:', ticket);
  }

  agruparPorEstado(tareas: TareaDto[]): KanbanColumn[] {
    // Mapa que agrupa por id de estado
    const mapa = new Map<number, { estado: DtoBase, tareas: TareaDto[] }>();

    // Inicializamos el mapa con los estados fijos
    for (const estado of this.estadosTarea) {
      mapa.set(estado.id, { estado, tareas: [] });
    }

    // Agrupar tareas por estado
    for (const tarea of tareas) {
      const estado = tarea.estadoTarea;

      if (estado && mapa.has(estado.id)) {
        mapa.get(estado.id)!.tareas.push(tarea);
      } else {
        // Si el estado no está en los fijos, podés agregarlo o ignorarlo
        if (estado) {
          mapa.set(estado.id, { estado, tareas: [tarea] });
        } else {
          // Tareas sin estado
          const sinEstado: DtoBase = { id: 0, descripcion: 'Sin Estado' };
          if (!mapa.has(0)) {
            mapa.set(0, { estado: sinEstado, tareas: [] });
          }
          mapa.get(0)!.tareas.push(tarea);
        }
      }
    }

    // Ordenamos según los estados fijos
    const columnas: KanbanColumn[] = [];

    for (const estado of this.estadosTarea) {
      const grupo = mapa.get(estado.id);
      if (grupo) {
        columnas.push({ estadoTarea: grupo.estado, tarea: grupo.tareas });
      }
    }

    // Agregamos los estados que no estaban en la lista fija
    for (const [id, grupo] of mapa.entries()) {
      if (!this.estadosTarea.some(e => e.id === id)) {
        columnas.push({ estadoTarea: grupo.estado, tarea: grupo.tareas });
      }
    }

    return columnas;
  }



}
